import React from "react";
import { ChevronDown } from "react-feather";
import {
  Footer,
  Group,
  PageSizeNativeSelect,
  PageSizeSelect,
  RangeLabel,
  PageNumber,
  PageOf,
  PagerIcon,
} from "./Pagination.styles";
import {
  PaginationFirstIcon,
  PaginationLastIcon,
  PaginationNextIcon,
  PaginationPrevIcon,
} from "./PaginationIcons";

export interface PaginationProps {
  /** Current page, 1-indexed. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Current page size. */
  pageSize: number;
  /** Total row count across all pages. */
  totalCount: number;
  /** Called with the new 1-indexed page when navigating. */
  onPageChange: (page: number) => void;
  /** Optional selectable page-size options (renders native select). */
  pageSizeOptions?: number[];
  /** Called when user selects a page size. */
  onPageSizeChange?: (pageSize: number) => void;
  /** Optional: called when the user clicks the page-size trigger. Parent renders its own menu. */
  onPageSizeClick?: () => void;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  pageCount,
  pageSize,
  totalCount,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  onPageSizeClick,
}) => {
  const startIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);
  const isFirst = page <= 1;
  const isLast = page >= pageCount;

  return (
    <Footer>
      <Group>
        {Array.isArray(pageSizeOptions) &&
        pageSizeOptions.length > 0 &&
        onPageSizeChange ? (
          <PageSizeNativeSelect
            aria-label="Rows per page"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </PageSizeNativeSelect>
        ) : (
          <PageSizeSelect
            type="button"
            onClick={onPageSizeClick}
            disabled={!onPageSizeClick}
          >
            {pageSize}
            <ChevronDown size={14} />
          </PageSizeSelect>
        )}
        <RangeLabel>
          {startIndex} - {endIndex} of {totalCount}
        </RangeLabel>
      </Group>

      <Group>
        <PagerIcon
          type="button"
          aria-label="First page"
          disabled={isFirst}
          onClick={() => onPageChange(1)}
        >
          <PaginationFirstIcon />
        </PagerIcon>
        <PagerIcon
          type="button"
          aria-label="Previous page"
          disabled={isFirst}
          onClick={() => onPageChange(page - 1)}
        >
          <PaginationPrevIcon />
        </PagerIcon>
        <PageNumber $active>{page}</PageNumber>
        <PageOf>of</PageOf>
        <PageNumber>{pageCount}</PageNumber>
        <PagerIcon
          type="button"
          aria-label="Next page"
          disabled={isLast}
          onClick={() => onPageChange(page + 1)}
        >
          <PaginationNextIcon />
        </PagerIcon>
        <PagerIcon
          type="button"
          aria-label="Last page"
          disabled={isLast}
          onClick={() => onPageChange(pageCount)}
        >
          <PaginationLastIcon />
        </PagerIcon>
      </Group>
    </Footer>
  );
};

export default Pagination;
